import { useState, useEffect, useRef, useCallback } from "react";
import apiClient from "../config/axiosConfig";

const DEBOUNCE_MS = 200;

export function useAlgoliaSearch({
                                     query          = "",
                                     facetFilters   = {},
                                     numericFilters = [],
                                     filters        = "",
                                     page           = 0,
                                     sort           = "relevance",
                                     hitsPerPage    = 40,
                                     expandTags     = true,
                                     enabled        = true,
                                 } = {}) {
    const [hits,       setHits]       = useState([]);
    const [facets,     setFacets]     = useState({});
    const [facetStats, setFacetStats] = useState({}); // <-- NEW STATE
    // What the index will accept a facet filter on, or null while unknown.
    // Algolia answers an UNREGISTERED attribute with zero hits rather than an
    // error, so a filter that can never match is otherwise indistinguishable
    // from an empty result — see algoliaSettingsService.
    const [facetable,  setFacetable]  = useState(null);
    // The order the backend ACTUALLY used. It falls back to relevance when a
    // sort replica does not exist yet (the backend can deploy before anyone
    // runs `npm run algolia:settings`), and saying so beats presenting
    // relevance order under the heading "Newest".
    const [appliedSort, setAppliedSort] = useState("relevance");
    const [nbHits,     setNbHits]     = useState(0);
    const [nbPages,    setNbPages]    = useState(0);
    const [loading,    setLoading]    = useState(false);
    const [error,      setError]      = useState(null);

    const debounceRef = useRef(null);
    // M-F1: monotonically increasing request id — only the latest in-flight
    // request may commit state, so out-of-order responses can't clobber newer
    // results, and bumping it on unmount blocks setState-after-unmount.
    const requestSeqRef = useRef(0);

    // Algolia facetFilters semantics: the OUTER array is AND'd, INNER arrays are
    // OR'd. Most facets keep value-level OR (e.g. WAV OR MP3 — a file has one
    // mimetype, so AND would match nothing). Tags are the exception: each selected
    // tag should narrow the result further, so we emit them as separate top-level
    // entries (AND). The backend then expands each standalone tag string into its
    // own OR-over-descendants group, preserving "include sub-tags".
    const buildAlgoliaFacetFilters = useCallback((facetObj) => {
        return Object.entries(facetObj)
            .filter(([, vals]) => vals && vals.length > 0)
            .flatMap(([field, vals]) =>
                field === "metadata.tags"
                    ? vals.map(v => `${field}:${v}`)      // AND each tag together
                    : [vals.map(v => `${field}:${v}`)]    // OR values within the facet
            );
    }, []);

    const doSearch = useCallback(async () => {
        if (!enabled) return;
        const requestId = ++requestSeqRef.current;
        setLoading(true);
        setError(null);
        try {
            const response = await apiClient.post("/api/search/algolia", {
                query,
                facetFilters: buildAlgoliaFacetFilters(facetFilters),
                numericFilters,
                filters,
                page,
                hitsPerPage,
                sort,
                // When on, the backend widens each tag facet filter to its
                // descendants so a parent tag also returns child-tagged files.
                expandTags,
                facets: [
                    "metadata.mimetype",
                    "metadata.tags",
                    "metadata.authors",
                    "metadata.license",
                    "metadata.projectIds",
                    "metadata.size",      // <-- ADDED to get stats
                    "metadata.duration"   // <-- ADDED to get stats
                ],
            });
            if (requestId !== requestSeqRef.current) return; // stale response
            setHits(response.data.hits);
            setFacets(response.data.facets ?? {});
            setFacetStats(response.data.facets_stats ?? {}); // <-- CAPTURE STATS
            setFacetable(response.data.facetableAttributes ?? null);
            setAppliedSort(response.data.sort ?? "relevance");
            setNbHits(response.data.nbHits ?? 0);
            setNbPages(response.data.nbPages ?? 0);
        } catch (err) {
            if (requestId !== requestSeqRef.current) return;
            console.error("[useAlgoliaSearch]", err);
            setError(err);
        } finally {
            if (requestId === requestSeqRef.current) setLoading(false);
        }
    }, [query, facetFilters, numericFilters, filters, page, hitsPerPage, sort, expandTags, enabled, buildAlgoliaFacetFilters]);

    useEffect(() => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(doSearch, DEBOUNCE_MS);
        return () => clearTimeout(debounceRef.current);
    }, [doSearch]);

    // M-F1: invalidate any in-flight request on unmount.
    useEffect(() => () => { requestSeqRef.current++; }, []);

    return { hits, facets, facetStats, facetable, appliedSort, nbHits, nbPages, loading, error, refetch: doSearch };
}